import { useSearchParams } from "@solidjs/router";
import type { StreamingChatId } from "@storytell/streaming";
import { StButton } from "@storytell/ui/buttons";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbChevronsRight } from "solid-icons/tb";
import type { Accessor } from "solid-js";
import { createEffect, createMemo, createResource, createSignal, onCleanup, Show } from "solid-js";
import { useChatScrollContextSafe } from "~/components/chat/contexts/ChatScrollContext";
import { useTOCContextSafe } from "~/components/chat/contexts/TOCContext";
import { useChatActivity } from "~/components/chat/hooks/useChatActivity";
import { getFullChatMarkdown } from "~/components/chat/utils/artifactUtils";
import { useI18n } from "~/i18n";
import { useWire } from "~/wire";

interface SidePanelToggleButtonProps {
  /** Whether the side panel is effectively hidden */
  isHidden: Accessor<boolean>;
}

/**
 * A toggle button that switches between show/hide for the side panel.
 * The icon rotates 180 degrees when transitioning between states.
 * Rendered as an overlay positioned outside the side panel.
 * When the panel is closed, only shown if opening scope-picker would show Navigate-this-chat content
 * (TOC, thread actions, chat artifacts, or edit) — not when that UI would be empty.
 */
export const SidePanelToggleButton = (props: SidePanelToggleButtonProps) => {
  const wire = useWire();
  const { t } = useI18n();
  const chatScroll = useChatScrollContextSafe();
  const tocContext = useTOCContextSafe();
  const activity = useChatActivity();
  const [searchParams] = useSearchParams<{ contentBlock?: string }>();
  const nav = wire.navigation;
  const layout = wire.ui.layout;

  // Track hover so we can keep tooltip open when user is using the timeline (timelinePointerActive) as if they were hovering the button
  const [isHovering, setIsHovering] = createSignal(false);
  const timelinePointerActive = () => chatScroll?.timelinePointerActive() ?? false;
  /** True when the current chat has a TOC in the "Navigate this Chat" section (headings from answer content). */
  const hasToc = () => (tocContext?.entries()?.length ?? 0) > 0;
  // Only show tooltip on timeline use when there is a TOC to navigate; otherwise tooltip only on hover
  const tooltipOpen = () => isHovering() || (props.isHidden() && timelinePointerActive() && hasToc());

  // Same sources ScopePickerPage draws from. The chat service's own id outlives the route — reading
  // it here reported content for a chat the panel had already stopped rendering.
  const activeChatId = createMemo((): StreamingChatId | undefined => {
    const pid = nav.state().primaryId;
    if (typeof pid === "string" && pid.startsWith("chat_")) return pid as StreamingChatId;
    const cs = chatScroll?.chatId();
    if (typeof cs === "string" && cs.startsWith("chat_")) return cs;
    return undefined;
  });

  const [artifactCount] = createResource(
    () => {
      const id = activeChatId();
      if (!id || !wire.auth.isReady()) return null;
      return id;
    },
    async (id: StreamingChatId) => {
      const r = await wire.artifacts.listByChatId(id);
      return r.isOk() ? r.value.artifacts.length : 0;
    }
  );

  /** True when opening scope-picker would show something in the hub (matches ScopePickerPage tabs / sections body). */
  const scopePickerWouldHaveContent = createMemo(() => {
    if (wire.navigation.state().editBlock) return true;
    if (searchParams.contentBlock) return true;
    if (hasToc()) return true;
    if (activity.opsCount() > 0) return true;
    const id = activeChatId();
    if (id && getFullChatMarkdown(id, wire).trim().length > 0) return true;
    if (artifactCount.loading) return true;
    const n = artifactCount();
    return typeof n === "number" && n > 0;
  });

  // Only scope-picker: the docked command center is empty until the prompt bar it mirrors registers
  // its composer on mount, so collapsing on that would close the panel just as it gains content.
  const sideIsEmpty = () => nav.state().sideId === "scope-picker" && !scopePickerWouldHaveContent();
  createEffect(() => layout.setSideEmpty(sideIsEmpty()));
  onCleanup(() => layout.setSideEmpty(false));

  // Check if main content is a page where the side panel (scope-picker / prompt library) is useful
  const isChatPage = () => {
    const primaryId = nav.state().primaryId;
    return !primaryId || primaryId.startsWith("chat_") || primaryId === "new";
  };

  const shouldShowButton = () => {
    if (nav.state().sideId && !sideIsEmpty()) return true;
    if (!isChatPage()) return false;
    if (props.isHidden()) return scopePickerWouldHaveContent();
    return true;
  };

  const handleClick = () => {
    const isHidden = props.isHidden();
    const sideId = nav.state().sideId;
    const chatPage = isChatPage();

    if (isHidden) {
      const CLOSE_TO_MIN_THRESHOLD = 50;
      if (layout.sideExpandedSize() < layout.SIDE_MIN + CLOSE_TO_MIN_THRESHOLD) {
        layout.resizeSide(layout.SIDE_DEFAULT);
      }

      if (!sideId) {
        if (chatPage) {
          const panelTab: "sections" | undefined = hasToc() ? "sections" : undefined;
          nav.openSidePanel("scope-picker", panelTab != null ? { panelTab } : undefined);
        }
      } else {
        nav.showSidePanel();
      }
    } else {
      nav.hideSidePanel();
    }
  };

  // When tooltip is open due to timeline use (and TOC exists), show "Show Panel to Navigate this Chat"; otherwise standard show/hide panel text
  const showingDueToTimeline = () => props.isHidden() && timelinePointerActive() && hasToc() && !isHovering();
  /** Returns the translation key for the current tooltip so a single t() call is used and logic stays in one place. */
  const getSidePanelTooltipKey = (): "title_show_panel_to_navigate_chat" | "title_show_panel" | "title_hide_panel" => {
    if (showingDueToTimeline()) return "title_show_panel_to_navigate_chat";
    if (props.isHidden()) return "title_show_panel";
    return "title_hide_panel";
  };
  const tooltipContent = () => t(getSidePanelTooltipKey());

  return (
    <Show when={shouldShowButton()}>
      <StTooltip
        content={tooltipContent()}
        placement="bottom"
        contentProps={{ class: "whitespace-nowrap" }}
        open={tooltipOpen()}
        onOpenChange={() => {
          // no-op -- tooltip open state managed via tooltipOpen() signal
        }}
      >
        <StTooltipTrigger
          as={StButton}
          icon={TbChevronsRight}
          onClick={handleClick}
          class="border border-primary-border bg-panel shadow-lg hover:bg-panel-hover"
          iconClass={`transition-transform duration-150 ${props.isHidden() ? "rotate-180" : ""}`}
          onPointerEnter={() => setIsHovering(true)}
          onPointerLeave={() => setIsHovering(false)}
        />
      </StTooltip>
    </Show>
  );
};
